import React, { useContext, useMemo, useState } from 'react';
import clone from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';

import * as Core from '@lexome/core';

import { BookContext } from '../Book';

export enum CURSOR_DIRECTION {
  FORWARDS,
  BACKWARDS,
}

export type Cursor = [number?, number?];

type BookNavigationContextType =
  | {
      loading: false;
      chapterLocation: Core.ChapterLocation;
      setChapterLocation: (location: Core.ChapterLocation) => void;
      chapter: Core.Chapter;
      fullChapter: Core.Chapter;
      cursor: Cursor;
      cursorDirection: CURSOR_DIRECTION;
      setCursor: (cursor: Cursor) => void;
      isPageRendering: boolean;
      expandCursorBounds: (n: number) => void;
      goToNextPage: () => void;
      goToPreviousPage: () => void;
      lowerRenderLimit: number;
      upperRenderLimit: number;
    }
  | {
      loading: true;
      chapterLocation?: Core.ChapterLocation;
      setChapterLocation?: (location: Core.ChapterLocation) => void;
      chapter?: Core.Chapter;
      fullChapter?: Core.Chapter;
      cursor?: Cursor;
      setCursor?: (cursor: Cursor) => void;
      cursorDirection?: CURSOR_DIRECTION;
      isPageRendering?: true;
      expandCursorBounds?: (n: number) => void;
      goToNextPage?: () => void;
      goToPreviousPage?: () => void;
      lowerRenderLimit?: number;
      upperRenderLimit?: number;
    };

const BookNavigationContext = React.createContext<BookNavigationContextType>({
  loading: true,
});

const getChapter = ({
  chapterLocation,
  content,
}: {
  chapterLocation: Core.ChapterLocation;
  content: Core.Chapter[];
}): Core.Chapter | undefined => {
  let chapter: Core.Chapter | undefined;

  for (const location of chapterLocation) {
    chapter = content[location];
  }

  return chapter;
};

// const getFirstSubchapterLocation = ({
//   book,
//   chapterLocation
// }: {
//   book: Book;
//   chapterLocation: ChapterLocation;
// }): ChapterLocation => {
//   let parentChapter = getChapter({
//     book,
//     chapterLocation
//   })

//   let subchapterLocation = [];

//   while(parentChapter.content[0].type === SECTION_TYPE.CHAPTER) {
//     subchapterLocation.push(0);
//     parentChapter = parentChapter.content[0];
//   }

//   return chapterLocation.concat(subchapterLocation);
// };

const shouldRenderParagraph = ({
  paragraph,
  upperRenderLimit,
  lowerRenderLimit,
}: {
  paragraph: Core.Paragraph;
  upperRenderLimit: number;
  lowerRenderLimit: number;
}): boolean => {
  if (lowerRenderLimit >= paragraph.offset + paragraph.length) {
    return false;
  }

  if (upperRenderLimit < paragraph.offset) {
    return false;
  }

  return true;
};

const truncateTextSection = ({
  textSection,
  lowerRenderLimit,
  upperRenderLimit,
}: {
  textSection: Core.TextSection;
  lowerRenderLimit: number;
  upperRenderLimit: number;
}): Core.TextSection => {
  const truncated = clone(textSection);

  const trunctatedContent = textSection.text.filter((word, n) => {
    const wordIndex = n + textSection.offset;

    if (wordIndex < lowerRenderLimit) {
      truncated.offset += 1;
      truncated.length -= 1;
    }
    if (wordIndex > upperRenderLimit) {
      truncated.length -= 1;
    }

    return wordIndex >= lowerRenderLimit && wordIndex <= upperRenderLimit;
  });

  truncated.text = trunctatedContent;

  return truncated;
};

export const truncateChapter = ({
  chapter,
  lowerRenderLimit,
  upperRenderLimit,
}: {
  chapter: Core.Chapter | undefined;
  lowerRenderLimit: number;
  upperRenderLimit: number;
}): Core.Chapter | undefined => {
  console.log(chapter);
  if (!chapter) {
    return undefined;
  }

  if (chapter.content[0].type === Core.SECTION_TYPE.CHAPTER) {
    return chapter;
  }

  const truncated = clone(chapter);

  truncated.content = (truncated.content as Core.Paragraph[]).filter((paragraph) =>
    shouldRenderParagraph({
      paragraph,
      upperRenderLimit,
      lowerRenderLimit,
    })
  );

  truncated.content.forEach((content) => {
    const paragraph = content as Core.Paragraph;
    paragraph.text = paragraph.text.map((textSection) =>
      truncateTextSection({
        upperRenderLimit,
        lowerRenderLimit,
        textSection,
      })
    );
  });

  return truncated;
};

const getNextChapterLocation = ({
  chapterLocation,
  content,
}: {
  chapterLocation: Core.ChapterLocation;
  content: Core.Chapter[];
}): Core.ChapterLocation => {
  const nextLocation = clone(chapterLocation);


  const currentChapter = getChapter({ chapterLocation, content });

  // If the next child is a subchapter, that should be the next location
  if (currentChapter?.content[0].type === Core.SECTION_TYPE.CHAPTER) {
    nextLocation.push(0);
    return nextLocation;
  }

  while (nextLocation.length) {
    const currentChapterLeafPosition = nextLocation.pop();
    const parentChapter = getChapter({
      chapterLocation: nextLocation,
      content,
    });

    if (typeof currentChapterLeafPosition === 'number' && !parentChapter) {
      nextLocation.push(currentChapterLeafPosition + 1);
      return nextLocation;
    }

    if (
      typeof currentChapterLeafPosition === 'number' &&
      (parentChapter?.content.length ?? 0) - 1 > currentChapterLeafPosition
    ) {
      nextLocation.push(currentChapterLeafPosition + 1);
      return nextLocation;
    }
  }

  return [];
};

const getPreviousChapterLocation = ({
  chapterLocation,
  content,
}: {
  chapterLocation: Core.ChapterLocation;
  content: Core.Chapter[];
}): Core.ChapterLocation => {
  const location = clone(chapterLocation);

  const isFirstChapter = location.every((val) => val === 0);

  if (isFirstChapter) {
    return [];
  }

  let lastLeaf = location.pop();
  while (lastLeaf === 0) {
    lastLeaf = location.pop();
  }

  if (typeof lastLeaf === 'number') {
    location.push(lastLeaf - 1);
    return location;
  } else {
    throw Error(
      `Unexpected error when finding previous chapter to ${JSON.stringify(chapterLocation)}`
    );
  }
};

const assertNumberDefined = (cursor: number | undefined, message: string): number => {
  if (cursor === undefined) {
    throw new Error(message);
  } else {
    return cursor;
  }
};

const NO_CURSOR_DEFINED_ERROR = 'NEITHER UPPER OR LOWER CURSOR DEFINED';
const MAX_PAGE_WORD_COUNT = 500;

export const BookNavigationProvider: React.FC = ({ children }) => {
  const { content } = useContext(BookContext);

  const [chapterLocation, setChapterLocation] = useState<number[] | undefined>([0]);

  const chapter = useMemo(
    () => (content && chapterLocation ? getChapter({ chapterLocation, content }) : undefined),
    [chapterLocation, content]
  );

  // Cursor tracks first and last word on page to support next and previous page operations
  const [cursor, setCursor] = useState<Cursor>([0, undefined]);

  const lowerRenderLimit = useMemo(() => {
    if (cursor[0] !== undefined) {
      return cursor[0];
    } else {
      const upperLimit = assertNumberDefined(cursor[1], NO_CURSOR_DEFINED_ERROR);
      return upperLimit - MAX_PAGE_WORD_COUNT;
    }
  }, [cursor]);

  const upperRenderLimit = useMemo(() => {
    if (cursor[1] !== undefined) {
      return cursor[1];
    } else {
      const lowerLimit = assertNumberDefined(cursor[0], NO_CURSOR_DEFINED_ERROR);
      return lowerLimit + MAX_PAGE_WORD_COUNT;
    }
  }, [cursor]);

  // Whether we are navigating forwards or backwards
  const [cursorDirection, setCursorDirection] = useState<CURSOR_DIRECTION>(
    CURSOR_DIRECTION.FORWARDS
  );

  // Page won't be rendered until cursor boundaries have been calculated.
  const [isPageRendering, setPageRendering] = useState<boolean>(true);

  const updateLowerCursor = debounce((n: number) => {
    setCursor((cursor) => {
      return [n, cursor[1]];
    });

    // If we reach start of chapter, show entire first page instead of truncating the content unnaturally.
    if (n === 0) {
      setCursorDirection(CURSOR_DIRECTION.FORWARDS);
      setCursor([0, undefined]);
    } else {
      setPageRendering(false);
    }
  }, 5);

  const updateUpperCursor = debounce((n: number) => {
    setCursor((cursor) => {
      return [cursor[0], n];
    });

    setPageRendering(false);
  }, 5);

  const truncatedChapter = useMemo(
    () =>
      truncateChapter({
        chapter,
        lowerRenderLimit,
        upperRenderLimit,
      }),
    [chapter, lowerRenderLimit, upperRenderLimit]
  );

  const expandCursorBounds = useMemo(() => {
    let lowerBound: number | undefined = cursor[0];
    let upperBound: number | undefined = cursor[1];

    const fn = (nodeId: number): void => {
      if (
        cursorDirection === CURSOR_DIRECTION.BACKWARDS &&
        (lowerBound === undefined || nodeId < lowerBound)
      ) {
        lowerBound = nodeId;
        updateLowerCursor(nodeId);
      }

      if (
        cursorDirection === CURSOR_DIRECTION.FORWARDS &&
        (upperBound === undefined || nodeId > upperBound)
      ) {
        upperBound = nodeId;
        updateUpperCursor(nodeId);
      }
    };

    return fn;
  }, [cursor, setCursor]);

  // const setChapterLocationManually = (location: ChapterLocation) => {
  //   setCursorDirection(CURSOR_DIRECTION.forwards);
  //   setChapterLocation(location);
  // }

  const next = (): void => {
    if (isEqual(chapterLocation, [])) {
      setChapterLocation([0]);
    }

    if (chapter && chapterLocation && cursor[1] && content) {
      setPageRendering(true);
      setCursorDirection(CURSOR_DIRECTION.FORWARDS);

      const isOnChapterPage = cursor[0] === -1 || cursor[1] === -1;
      const isLeafChapter = chapter.content[0].type === Core.SECTION_TYPE.PARAGRAPH;
      const isAtChapterEnd = cursor[1] === chapter.length - 1;

      if (isOnChapterPage && isLeafChapter) {
        setCursor([0, undefined]);
      } else if (isAtChapterEnd || !isLeafChapter) {
        const nextChapterLocation = getNextChapterLocation({ chapterLocation, content });
        setChapterLocation(nextChapterLocation);
        setCursor([-1, -1]);
      } else {
        setCursor([cursor[1] + 1, undefined]);
      }
    }
  };

  const goToPreviousPage = (): void => {
    if (isEqual(chapterLocation, [0]) && isEqual(cursor, [-1, -1])) {
      setChapterLocation([]);
    } else if (chapter && chapterLocation && cursor[0] !== undefined && content) {
      setPageRendering(true);
      setCursorDirection(CURSOR_DIRECTION.BACKWARDS);

      const isOnChapterPage = cursor[0] === -1 || cursor[1] === -1;

      if (isOnChapterPage && !isEqual(chapterLocation, [0])) {
        const previousChapterLocation = getPreviousChapterLocation({
          chapterLocation,
          content,
        });
        const previousChapter = getChapter({ chapterLocation: previousChapterLocation, content });
        setChapterLocation(previousChapterLocation);
        setCursor([undefined, (previousChapter?.length ?? 0) - 1]);
      } else if (cursor[0] === 0) {
        setCursor([-1, -1]);
      } else {
        setCursor([undefined, cursor[0] - 1]);
      }
    }
  };

  return (
    <BookNavigationContext.Provider
      value={{
        loading: false,
        chapter: truncatedChapter!,
        fullChapter: chapter!,
        chapterLocation: chapterLocation!,
        setChapterLocation,
        cursor,
        setCursor,
        cursorDirection,
        expandCursorBounds,
        goToNextPage: next,
        goToPreviousPage,
        upperRenderLimit,
        lowerRenderLimit,
        isPageRendering,
      }}>
      {children}
    </BookNavigationContext.Provider>
  );
};

export const useBookNavigation = (): BookNavigationContextType => {
  return useContext(BookNavigationContext);
};
